import React, { useContext, useEffect, useRef, useState } from "react";
import "./Navbar.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/logo/logo.svg";
import { Context } from "../../context/Context";
import { homeRouteFor } from "../../access/core/routePolicies";
import { useTheme } from "../../context/ThemeContext";
import { translations } from "../../utils/lang";
import { landingContent } from "../landing/content/landingContent";
import {
  Globe,
  LogOut,
  Moon,
  Sun,
  Menu as MenuIcon,
  X as CloseIcon,
} from "lucide-react";

export const Navbar = () => {
  const {
    lang,
    setLang,
    handleTokenExpiration,
    signOut,
    isAuthenticated,
    currentUserInfo,
  } = useContext(Context);
  const { theme, toggleTheme } = useTheme();
  const languageRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const marketingRoutes = ["/", "/solutions", "/pricing", "/contactus", "/terms", "/privacy","/refund", "/feedback","/signin"];
  const isLanding = marketingRoutes.includes(location.pathname);
  const t = (landingContent[lang] || landingContent.en).nav;

  const language_toggle = () => {
    if (languageRef.current) {
      languageRef.current.classList.toggle("language-display");
    }
  };

  const setNavLang = (nextLang) => {
    setLang(nextLang);
    localStorage.setItem("lang", nextLang);
    language_toggle();
  };

  const signOutHandler = () => {
    // an explicit sign-out ends the session at once (handleTokenExpiration first asks the server)
    (signOut || handleTokenExpiration)();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        languageRef.current.classList.remove("language-display");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Landing route for the signed-in user (never one they cannot open); the
  // restaurants page while the user is still loading, as before.
  const homeLink = isAuthenticated
    ? homeRouteFor(currentUserInfo) || (currentUserInfo?.role ? "/" : "/auth/restaurants")
    : "/";

  const marketingLinks = [
    { key: "home", label: t.home, to: "/" },
    { key: "solutions", label: t.solutions, to: "/solutions" },
    { key: "plans", label: t.plans, to: "/pricing" },
    { key: "contact", label: t.contact, to: "/contactus" },
  ];

  const renderMarketingLink = (item, className) => {
    if (!item.to) {
      return (
        <span className={className} key={item.key}>
          {item.label}
        </span>
      );
    }
    const isActive = location.pathname === item.to;
    return (
      <Link
        to={item.to}
        className={`${className} ${isActive ? "active" : ""}`}
        key={item.key}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <div
      className={`navbar  ${isLanding?"navbar-landing":""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <div className="navbar-inner">
        <Link to={"/"} className="link center navbar-brand">
          <img src={Logo} alt="LUD" className="nav-logo" />
        </Link>

        {isLanding && (
          <div className="navbar-links">
            {marketingLinks.map((item) =>
              renderMarketingLink(item, "navbar-link"),
            )}
          </div>
        )}

        <div className="navbar-right">
          <button
            type="button"
            className="mobile-menu-toggle"
            aria-label={t.openMenu}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <CloseIcon size={18} /> : <MenuIcon size={18} />}
          </button>
          <div className="view-controls" aria-label={t.displaySettings}>
            <button
              type="button"
              className="view-toggle theme-toggle"
              aria-label={t.switchTheme}
              onClick={toggleTheme}
            >
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <button
              type="button"
              className="view-toggle lang-toggle"
              aria-label={t.changeLanguage}
              onClick={language_toggle}
            >
              <Globe size={17} />
            </button>
            <div ref={languageRef} className="language-list-container">
              <div className="language-list">
                <p
                  className={lang === "ar" ? "language-list-p-align" : ""}
                  onClick={() => {
                    setNavLang("ar");
                  }}
                >
                  {translations[lang].arLang}
                </p>
                <p
                  className={lang === "ar" ? "language-list-p-align" : ""}
                  onClick={() => {
                    setNavLang("en");
                  }}
                >
                  {translations[lang].enLang}
                </p>
              </div>
            </div>
          </div>

          {isAuthenticated ? (
            <>
              {isLanding && (
                <Link
                  to={homeLink}
                  className="landing-btn landing-btn-primary navbar-cta"
                >
                  {t.dashboard}
                </Link>
              )}

              <button
                type="button"
                className="view-toggle"
                aria-label={t.changeLanguage}
                onClick={signOutHandler}
              >
                <LogOut size={17} />
              </button>
            </>
          ) : (
            <>
              <Link to="/signin" className="navbar-login-link">
                {t.login}
              </Link>
              <Link
                to="/contactus"
                className="landing-btn landing-btn-primary navbar-cta"
              >
                {t.start}
              </Link>
            </>
          )}
        </div>

        <div className={`mobile-panel ${mobileOpen ? "open" : ""}`}>
          {marketingLinks.map((item) =>
            renderMarketingLink(item, "mobile-panel-link"),
          )}
          {isAuthenticated ? (
            <div className="mobile-panel-actions mobile-panel-actions-single">
              <Link to={homeLink} className="landing-btn landing-btn-primary">
                {t.dashboard}
              </Link>
            </div>
          ) : (
            <div className="mobile-panel-actions">
              <button
                type="button"
                className="mobile-login"
                onClick={() => navigate("/signin")}
              >
                {t.login}
              </button>
              <Link to="/contactus" className="landing-btn landing-btn-primary">
                {t.start}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
